import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "./layout";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doFetch } from "../redux/api/actions";
import { GET_TOUR } from "../redux/api/constants";
import { apiAddress } from "../config";
import { loadTourTracks, playTrackRequest, setCurrentTour } from "../redux/audio/actions";
import { useTranslation } from "react-i18next";

const categoryTranslationKeys = {
    "General": "category-general",
    "Ghost Tour": "category-ghost-tour",
    "Historical": "category-historical",
    "Pop Culture": "category-pop-culture",
    "Family History": "category-family-history",
    "Food & Drink": "category-food-drink",
    "Architecture": "category-architecture",
    "Nature & Parks": "category-nature-parks",
    "Art & Culture": "category-art-culture",
};

const Tour = () => {
    const { t } = useTranslation();
    const { tourId } = useParams();
    const tour = useSelector(state => state.API.tour);
    const accessToken = useSelector(state => state.Auth.accessToken);
    const dispatch = useDispatch();

    const [tourData, setTourData] = useState(null);
    const [tourLoading, setTourLoading] = useState(false);
    const [tourError, setTourError] = useState(null);

    useEffect(() => {
        dispatch(doFetch(GET_TOUR, `tours/${tourId}`, null));
    }, [tourId, dispatch]);

    useEffect(() => {
        let isCancelled = false;
        const load = async () => {
            setTourLoading(true);
            setTourError(null);
            try {
                const headers = {};
                if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
                const res = await fetch(`${apiAddress}/map/tours/${tourId}`, { headers });
                if (!res.ok) {
                    const msg = await res.text();
                    throw new Error(msg || `Failed to load tour (${res.status})`);
                }
                const data = await res.json();
                if (!isCancelled) setTourData(data);
            } catch (e) {
                if (!isCancelled) setTourError(String(e?.message || e));
            } finally {
                if (!isCancelled) setTourLoading(false);
            }
        };
        load();
        return () => { isCancelled = true; };
    }, [tourId, accessToken]);

    const stops = useMemo(() => {
        const list = tourData?.tracks || [];
        return [...list].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
    }, [tourData]);

    const startStop = useCallback((trackId) => {
        if (!accessToken) return;
        if (!trackId) return;
        dispatch(setCurrentTour({ tourId, tourTitle: tourData?.tourTitle || tour?.title || '' }));
        dispatch(loadTourTracks(
            stops.map((s) => ({
                trackId: s.trackId,
                title: s.title,
                tourTitle: tourData?.tourTitle || tour?.title || '',
            }))
        ));
        dispatch(playTrackRequest(trackId, tourId));
    }, [accessToken, dispatch, stops, tourId, tourData?.tourTitle, tour?.title]);

    const imageUrls = tour?.image_urls ? tour.image_urls.split(",").filter(url => url.trim()) : [];
    const [activeIndex, setActiveIndex] = React.useState(0);

    const formatDuration = (seconds) => {
        if (!seconds) return null;
        const mins = Math.round(seconds / 60);
        if (mins < 60) return `${mins} min`;
        const hours = Math.floor(mins / 60);
        const rem = mins % 60;
        return rem ? `${hours} ${t('hours-label')} ${rem} min` : `${hours} ${t('hours-label')}`;
    };

    const tourDurationLabel = formatDuration(tourData?.totalDurationSeconds);

    const updateActiveIndex = (increment) => {
        if (imageUrls.length === 0) return;
        const maxIndex = imageUrls.length - 1;
        let newIndex = activeIndex + increment;
        if (newIndex < 0) newIndex = maxIndex;
        else if (newIndex > maxIndex) newIndex = 0;
        setActiveIndex(newIndex);
    };

    return (
        <Layout selected="tour">
            {tour && (
                <div className="tour-page">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
	                                <Link to="/">{t('home')}</Link>
                            </li>
                            <li className="breadcrumb-item active">{tour.title}</li>
                        </ol>
                    </nav>

                    <div className="tour-layout">
                        <div className="tour-main">
                            {imageUrls.length > 0 && (
                                <div className="tour-gallery mb-4">
                                    <div className="carousel slide">
                                        <div className="carousel-inner rounded">
                                            {imageUrls.map((imageUrl, index) => (
                                                <div 
                                                    className={index === activeIndex ? "carousel-item active" : "carousel-item"} 
                                                    key={index}
                                                >
                                                    <img 
                                                        src={imageUrl} 
                                                        className="d-block w-100"
                                                        style={{ maxHeight: '450px', objectFit: 'cover' }}
                                                        alt={`${tour.title} - ${index + 1}`} 
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {imageUrls.length > 1 && (
                                            <>
                                                <button className="carousel-control-prev" type="button" onClick={() => updateActiveIndex(-1)}>
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                </button>
                                                <button className="carousel-control-next" type="button" onClick={() => updateActiveIndex(1)}>
                                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <h1 className="tour-title">{tour.title}</h1>
                            
                            <div className="tour-meta">
                                {tourDurationLabel && (
                                    <span className="meta-item">
                                        <i className="bi bi-clock"></i> {tourDurationLabel}
                                    </span>
                                )}
                                <span className="meta-item">
	                                    <i className="bi bi-geo-alt"></i> {t('stops-count', { count: tourData?.trackCount || stops.length || 0 })}
                                </span>
                                {tour.category && (
                                    <span className="meta-item">
	                                        <i className="bi bi-tag"></i> {t(categoryTranslationKeys[tour.category] || tour.category)}
                                    </span>
                                )}
                            </div>

                            <div className="tour-description">
	                                <h3>{t('tour-about-this-tour')}</h3>
                                <p>{tour.description}</p>
                            </div>

                            <div className="tour-description">
                                <div className="d-flex justify-content-between align-items-center">
	                                    <h3 className="mb-0">{t('tour-stops')}</h3>
                                    {accessToken ? (
                                        <button
                                            className="btn btn-outline-dark btn-sm"
                                            onClick={() => startStop(stops?.[0]?.trackId)}
                                            disabled={tourLoading || !stops?.length}
                                        >
	                                            {t('tour-play-from-start')}
                                        </button>
                                    ) : (
                                        <Link className="btn btn-outline-dark btn-sm" to="/signin">
	                                            {t('tour-sign-in-to-listen')}
                                        </Link>
                                    )}
                                </div>
                                {tourLoading ? (
	                                    <p className="text-muted mt-2 mb-0">{t('tour-loading-stops')}</p>
                                ) : tourError ? (
                                    <p className="text-danger mt-2 mb-0">{tourError}</p>
                                ) : stops.length === 0 ? (
	                                    <p className="text-muted mt-2 mb-0">{t('tour-no-stops-yet')}</p>
                                ) : (
                                    <div className="list-group mt-3">
                                        {stops.map((s, idx) => (
                                            <button
                                                key={s.trackId}
                                                type="button"
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                onClick={() => startStop(s.trackId)}
                                            >
                                                <div className="text-start">
                                                    <div className="fw-semibold">{idx + 1}. {s.title}</div>
                                                    <div className="text-muted small">
                                                        {s.lat?.toFixed ? `${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}` : ''}
                                                    </div>
                                                </div>
                                                <span className="text-muted small">
	                                                    {accessToken ? t('play') : t('signin')}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {tour.tourguide && (
                                <div className="host-section">
	                                    <h3>{t('tour-guide')}</h3>
                                    <div className="host-card">
                                        {tour.tourguide.avatar_url && (
                                            <img 
                                                src={tour.tourguide.avatar_url} 
                                                alt={tour.tourguide.name}
                                                className="host-avatar"
                                            />
                                        )}
                                        <div className="host-info">
                                            <h4>{tour.tourguide.name}</h4>
                                            {tour.tourguide.bio && (
                                                <p className="text-muted">{tour.tourguide.bio}</p>
                                            )}
                                            <div className="host-contact">
                                                {tour.tourguide.email && (
                                                    <a href={`mailto:${tour.tourguide.email}`}>
                                                        <i className="bi bi-envelope"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.facebook && (
                                                    <a href={tour.tourguide.facebook} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-facebook"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.instagram && (
                                                    <a href={tour.tourguide.instagram} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-instagram"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.webpage && (
                                                    <a href={tour.tourguide.webpage} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-globe"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <aside className="tour-sidebar">
                            <div className="listen-card">
                                <div className="listen-card-header">
                                    <span className="listen-card-icon">
                                        <i className="bi bi-headphones"></i>
                                    </span>
                                    <div>
	                                        <h2>{t('tour-start-listening')}</h2>
	                                        <p>{t('tour-stops-ready-on-map', { count: tourData?.trackCount || stops.length || 0 })}</p>
                                    </div>
                                </div>

                                <div className="listen-card-stats">
                                    <div>
	                                        <strong>{tourDurationLabel || t('text')}</strong>
	                                        <span>{t('duration')}</span>
                                    </div>
                                    <div>
                                        <strong>{tourData?.trackCount || stops.length || 0}</strong>
	                                        <span>{t('stops-label')}</span>
                                    </div>
                                </div>

                                {accessToken ? (
                                    <button
                                        className="listen-card-primary"
                                        onClick={() => startStop(stops?.[0]?.trackId)}
                                        disabled={tourLoading || !stops?.length}
                                    >
                                        <i className="bi bi-play-fill"></i>
	                                        {t('tour-play-from-start')}
                                    </button>
                                ) : (
                                    <Link className="listen-card-primary" to="/signin">
                                        <i className="bi bi-person-circle"></i>
	                                        {t('tour-sign-in-to-listen')}
                                    </Link>
                                )}

                                {stops?.[0] && (
                                    <Link
                                        className="listen-card-secondary"
                                        to={`/?lat=${stops[0].lat}&lng=${stops[0].lng}&zoom=16`}
                                    >
                                        <i className="bi bi-map"></i>
	                                        {t('tour-open-first-stop-map')}
                                    </Link>
                                )}
                            </div>
                        </aside>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Tour;
